// Path: C:\gestion-copro\src\components\admin\CreerApporteurModal.tsx
// Session 188 : Modal de gestion des apporteurs d'affaires
// Deux modes :
//   1. Promouvoir un utilisateur VIGIE existant (ajoute apporteurInfo sans changer le role)
//   2. Créer un nouveau compte apporteur dédié (Cloud Function createApporteurAccount)
// Appelée depuis CrmPipelinePage

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  X,
  Handshake,
  Loader2,
  CheckCircle2,
  UserPlus,
  Eye,
  EyeOff,
  Search,
  UserCheck,
  Crown,
  ExternalLink,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface ApporteurExistant {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  identifiant: string;
  role: string;
  status: string;
}

interface VigieUser {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  vigieInfo?: { nomCopropriete?: string };
  hasApporteurInfo: boolean;
}

interface CreerApporteurModalProps {
  onFermer: () => void;
  onCree: () => void;
}

// ============================================
// COMPOSANT
// ============================================

const CreerApporteurModal: React.FC<CreerApporteurModalProps> = ({ onFermer, onCree }) => {
  const navigate = useNavigate();

  // Mode actif : 'promouvoir' ou 'creer'
  const [mode, setMode] = useState<'promouvoir' | 'creer'>('promouvoir');

  // === État partagé ===
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // === Apporteurs existants ===
  const [apporteurs, setApporteurs] = useState<ApporteurExistant[]>([]);
  const [loadingApporteurs, setLoadingApporteurs] = useState(true);

  // === Mode Promouvoir ===
  const [vigieUsers, setVigieUsers] = useState<VigieUser[]>([]);
  const [loadingVigies, setLoadingVigies] = useState(true);
  const [searchVigie, setSearchVigie] = useState('');
  const [selectedVigie, setSelectedVigie] = useState<VigieUser | null>(null);
  const [promoIdentifiant, setPromoIdentifiant] = useState('');
  const [promoTelephone, setPromoTelephone] = useState('');

  // === Mode Créer ===
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [identifiant, setIdentifiant] = useState('');
  const [telephone, setTelephone] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Auto-générer l'identifiant en mode Créer
  useEffect(() => {
    if (mode === 'creer' && prenom && !identifiant) {
      setIdentifiant(
        prenom
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, '_')
      );
    }
  }, [prenom, mode]);

  // Charger les apporteurs existants (role === 'apporteur' OU apporteurInfo présent)
  useEffect(() => {
    const charger = async () => {
      try {
        // Chercher par role === 'apporteur'
        const q1 = query(collection(db, 'users'), where('role', '==', 'apporteur'));
        const snap1 = await getDocs(q1);

        // Chercher tous les users qui ont apporteurInfo (VIGIE promus)
        // On ne peut pas faire where sur un champ imbriqué facilement,
        // donc on filtre côté client parmi les VIGIE
        const q2 = query(collection(db, 'users'), where('role', '==', 'vigie'));
        const snap2 = await getDocs(q2);

        const liste: ApporteurExistant[] = [];
        const seen = new Set<string>();

        for (const d of snap1.docs) {
          const data = d.data();
          if (data.apporteurInfo?.identifiant) {
            liste.push({
              id: d.id,
              nom: data.nom || '',
              prenom: data.prenom || '',
              email: data.email || '',
              identifiant: data.apporteurInfo.identifiant,
              role: data.role,
              status: data.status || 'active',
            });
            seen.add(d.id);
          }
        }

        for (const d of snap2.docs) {
          if (seen.has(d.id)) continue;
          const data = d.data();
          if (data.apporteurInfo?.identifiant) {
            liste.push({
              id: d.id,
              nom: data.nom || '',
              prenom: data.prenom || '',
              email: data.email || '',
              identifiant: data.apporteurInfo.identifiant,
              role: data.role,
              status: data.status || 'active',
            });
          }
        }

        setApporteurs(liste);
      } catch (err) {
        console.error('Erreur chargement apporteurs:', err);
      } finally {
        setLoadingApporteurs(false);
      }
    };
    charger();
  }, []);

  // Charger les utilisateurs VIGIE (pour le mode Promouvoir)
  useEffect(() => {
    const charger = async () => {
      try {
        const q = query(collection(db, 'users'), where('role', '==', 'vigie'));
        const snapshot = await getDocs(q);
        const liste: VigieUser[] = snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            nom: data.nom || '',
            prenom: data.prenom || '',
            email: data.email || '',
            vigieInfo: data.vigieInfo,
            hasApporteurInfo: !!data.apporteurInfo?.identifiant,
          };
        });
        setVigieUsers(liste);
      } catch (err) {
        console.error('Erreur chargement VIGIE:', err);
      } finally {
        setLoadingVigies(false);
      }
    };
    charger();
  }, []);

  // VIGIE filtrés par recherche et qui ne sont pas déjà apporteurs
  const vigiesFiltres = vigieUsers.filter((v) => {
    if (v.hasApporteurInfo) return false; // Déjà promu
    if (!searchVigie) return true;
    const s = searchVigie.toLowerCase();
    return (
      v.nom.toLowerCase().includes(s) ||
      v.prenom.toLowerCase().includes(s) ||
      v.email.toLowerCase().includes(s)
    );
  });

  // === PROMOUVOIR un VIGIE existant ===
  const handlePromouvoir = async () => {
    if (!selectedVigie || !promoIdentifiant.trim()) {
      setError('Sélectionne un utilisateur et renseigne l\'identifiant CRM.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const userRef = doc(db, 'users', selectedVigie.id);
      await updateDoc(userRef, {
        apporteurInfo: {
          identifiant: promoIdentifiant.trim().toLowerCase(),
          nomComplet: `${selectedVigie.prenom} ${selectedVigie.nom}`.toUpperCase(),
          telephone: promoTelephone.trim() || '',
          commission: 0.25,
          dateDebut: Timestamp.now(),
        },
        updatedAt: Timestamp.now(),
      });

      setSuccess(
        `${selectedVigie.prenom} ${selectedVigie.nom} est maintenant apporteur (${promoIdentifiant}). ` +
        `Il conserve son accès VIGIE et peut aussi accéder à /apporteur.`
      );

      // Mettre à jour la liste locale
      setApporteurs((prev) => [
        ...prev,
        {
          id: selectedVigie.id,
          nom: selectedVigie.nom,
          prenom: selectedVigie.prenom,
          email: selectedVigie.email,
          identifiant: promoIdentifiant.trim().toLowerCase(),
          role: 'vigie',
          status: 'active',
        },
      ]);

      // Marquer le VIGIE comme déjà promu
      setVigieUsers((prev) =>
        prev.map((v) =>
          v.id === selectedVigie.id ? { ...v, hasApporteurInfo: true } : v
        )
      );

      setTimeout(() => onCree(), 2500);
    } catch (err: any) {
      console.error('Erreur promotion apporteur:', err);
      setError(err.message || 'Erreur lors de la promotion.');
    } finally {
      setSaving(false);
    }
  };

  // === CRÉER un nouveau compte ===
  const handleCreer = async () => {
    if (!prenom.trim() || !nom.trim() || !email.trim() || !password.trim() || !identifiant.trim()) {
      setError('Tous les champs marqués * sont requis.');
      return;
    }
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const createApporteur = httpsCallable<
        {
          email: string;
          password: string;
          nom: string;
          prenom: string;
          identifiant: string;
          telephone?: string;
        },
        { success: boolean; uid: string; email: string; message: string }
      >(functions, 'createApporteurAccount');

      const result = await createApporteur({
        email: email.trim(),
        password,
        nom: nom.trim(),
        prenom: prenom.trim(),
        identifiant: identifiant.trim().toLowerCase(),
        telephone: telephone.trim() || undefined,
      });

      setSuccess(result.data.message);

      setApporteurs((prev) => [
        ...prev,
        {
          id: result.data.uid,
          nom: nom.trim(),
          prenom: prenom.trim(),
          email: email.trim(),
          identifiant: identifiant.trim().toLowerCase(),
          role: 'apporteur',
          status: 'active',
        },
      ]);

      setTimeout(() => onCree(), 2500);
    } catch (err: any) {
      console.error('Erreur création apporteur:', err);
      setError(err.message || 'Erreur lors de la création du compte.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Handshake size={20} className="text-blue-600" />
            <h2 className="text-lg font-bold text-gray-800">Gérer les apporteurs</h2>
          </div>
          <button onClick={onFermer} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Apporteurs existants */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Apporteurs actifs
            </h3>
            {loadingApporteurs ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-3">
                <Loader2 size={14} className="animate-spin" />
                Chargement...
              </div>
            ) : apporteurs.length === 0 ? (
              <p className="text-sm text-gray-400 py-3">Aucun apporteur pour le moment.</p>
            ) : (
              <div className="space-y-2">
                {apporteurs.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2"
                  >
                    <div>
                      <p className="font-medium text-sm text-gray-800">
                        {a.prenom} {a.nom}
                        <span className="ml-2 text-xs text-blue-600 font-semibold">
                          ({a.identifiant})
                        </span>
                      </p>
                      <p className="text-xs text-gray-500">
                        {a.email}
                        {a.role === 'vigie' && (
                          <span className="ml-2 text-amber-600 font-semibold">VIGIE + Apporteur</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { onFermer(); navigate(`/admin-saas/apporteur/${a.id}`); }}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 px-2 py-1 rounded-lg transition-colors font-medium"
                        title="Voir la fiche complète"
                      >
                        <ExternalLink size={12} /> Fiche
                      </button>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          a.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {a.status === 'active' ? 'Actif' : a.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Séparateur + Choix du mode */}
          {!success && (
            <div className="border-t pt-4">
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => { setMode('promouvoir'); setError(null); }}
                  className={`flex-1 py-2 px-3 text-sm font-semibold rounded-lg border-2 transition-colors ${
                    mode === 'promouvoir'
                      ? 'bg-amber-50 text-amber-700 border-amber-400'
                      : 'bg-gray-50 text-gray-500 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <Crown size={14} className="inline mr-1.5 -mt-0.5" />
                  Promouvoir un VIGIE
                </button>
                <button
                  onClick={() => { setMode('creer'); setError(null); }}
                  className={`flex-1 py-2 px-3 text-sm font-semibold rounded-lg border-2 transition-colors ${
                    mode === 'creer'
                      ? 'bg-blue-50 text-blue-700 border-blue-400'
                      : 'bg-gray-50 text-gray-500 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <UserPlus size={14} className="inline mr-1.5 -mt-0.5" />
                  Nouveau compte
                </button>
              </div>

              {/* Message erreur */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* ========== MODE PROMOUVOIR ========== */}
              {mode === 'promouvoir' && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">
                    Sélectionne un utilisateur VIGIE existant pour lui donner aussi le rôle d'apporteur.
                    Il conservera son accès VIGIE.
                  </p>

                  {/* Recherche VIGIE */}
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchVigie}
                      onChange={(e) => setSearchVigie(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                      placeholder="Rechercher par nom ou email..."
                    />
                  </div>

                  {/* Liste VIGIE */}
                  {loadingVigies ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                      <Loader2 size={14} className="animate-spin" />
                      Chargement des utilisateurs VIGIE...
                    </div>
                  ) : vigiesFiltres.length === 0 ? (
                    <p className="text-sm text-gray-400 py-2">
                      {vigieUsers.filter((v) => !v.hasApporteurInfo).length === 0
                        ? 'Tous les VIGIE sont déjà apporteurs.'
                        : 'Aucun VIGIE correspondant.'}
                    </p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto space-y-1 border rounded-lg p-1">
                      {vigiesFiltres.map((v) => (
                        <button
                          key={v.id}
                          onClick={() => {
                            setSelectedVigie(v);
                            setPromoIdentifiant(
                              v.prenom
                                .toLowerCase()
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .replace(/\s+/g, '_')
                            );
                          }}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                            selectedVigie?.id === v.id
                              ? 'bg-amber-100 border border-amber-300'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <p className="font-medium text-gray-800">
                            {v.prenom} {v.nom}
                          </p>
                          <p className="text-xs text-gray-500">
                            {v.email}
                            {v.vigieInfo?.nomCopropriete && (
                              <span className="ml-1 text-gray-400">
                                — {v.vigieInfo.nomCopropriete}
                              </span>
                            )}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Champs après sélection */}
                  {selectedVigie && (
                    <div className="bg-amber-50 rounded-lg p-3 space-y-3 border border-amber-200">
                      <div className="flex items-center gap-2 text-sm">
                        <UserCheck size={16} className="text-amber-600" />
                        <span className="font-semibold text-amber-800">
                          {selectedVigie.prenom} {selectedVigie.nom}
                        </span>
                        <span className="text-amber-600">({selectedVigie.email})</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Identifiant CRM *
                          </label>
                          <input
                            type="text"
                            value={promoIdentifiant}
                            onChange={(e) =>
                              setPromoIdentifiant(
                                e.target.value.toLowerCase().replace(/\s+/g, '_')
                              )
                            }
                            className="w-full px-3 py-2 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-amber-500"
                            placeholder="alain"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Téléphone</label>
                          <input
                            type="tel"
                            value={promoTelephone}
                            onChange={(e) => setPromoTelephone(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                            placeholder="06 12 34 56 78"
                          />
                        </div>
                      </div>

                      <p className="text-[10px] text-amber-600">
                        Commission : 25% du CA HT — Son rôle VIGIE ne change pas, il gagne en plus
                        l'accès à l'espace Partenaire.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ========== MODE CRÉER ========== */}
              {mode === 'creer' && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">
                    Crée un nouveau compte dédié pour un partenaire qui n'est pas déjà client VIGIE.
                  </p>

                  {/* Prénom + Nom */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Prénom *</label>
                      <input
                        type="text"
                        value={prenom}
                        onChange={(e) => setPrenom(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="Alain"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Nom *</label>
                      <input
                        type="text"
                        value={nom}
                        onChange={(e) => setNom(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="LAZARSKI"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email de connexion *</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="alain@example.com"
                    />
                  </div>

                  {/* Mot de passe */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Mot de passe initial *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="6 caractères minimum"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Identifiant + Téléphone */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Identifiant CRM *</label>
                      <input
                        type="text"
                        value={identifiant}
                        onChange={(e) =>
                          setIdentifiant(e.target.value.toLowerCase().replace(/\s+/g, '_'))
                        }
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder="alain"
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        Utilisé pour filtrer les prospects
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Téléphone</label>
                      <input
                        type="tel"
                        value={telephone}
                        onChange={(e) => setTelephone(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Message succès */}
          {success && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
              <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-700">{success}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onFermer}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {success ? 'Fermer' : 'Annuler'}
          </button>
          {!success && mode === 'promouvoir' && (
            <button
              onClick={handlePromouvoir}
              disabled={saving || !selectedVigie || !promoIdentifiant.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Crown size={16} />
              )}
              Promouvoir en apporteur
            </button>
          )}
          {!success && mode === 'creer' && (
            <button
              onClick={handleCreer}
              disabled={
                saving ||
                !prenom.trim() ||
                !nom.trim() ||
                !email.trim() ||
                !password.trim() ||
                !identifiant.trim()
              }
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <UserPlus size={16} />
              )}
              Créer le compte
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreerApporteurModal;
